import type { Table } from "dexie";

/**
 * Lazy per-record migration for stores whose records are too large for a Dexie
 * `upgrade()` callback: that is one blocking transaction over the whole store,
 * and a failure there leaves the database unreachable for the new build.
 */

export interface VersionedRecord {
  /** Absent on records written before the store carried a version. */
  schemaVersion?: number;
}

/** Brings a record from `from` to `from + 1`. */
export interface MigrationStep<T> {
  from: number;
  /**
   * Returns the record's new shape; the runner writes it back with the new
   * version, so a step that throws leaves the record as it was.
   */
  upgrade: (record: T) => Promise<T>;
}

/** Steps must be in ascending order; the first one defines the unstamped version. */
function versionOf<T extends VersionedRecord>(
  record: T,
  steps: MigrationStep<T>[],
): number {
  return record.schemaVersion ?? steps[0].from;
}

export function needsMigration<T extends VersionedRecord>(
  record: T,
  steps: MigrationStep<T>[],
): boolean {
  return steps.some((step) => step.from === versionOf(record, steps));
}

export async function migrateRecord<T extends VersionedRecord, K>(
  table: Table<T, K>,
  key: K,
  steps: MigrationStep<T>[],
): Promise<T | undefined> {
  let record = await table.get(key);
  if (!record) return undefined;

  for (const step of steps) {
    if (versionOf(record, steps) !== step.from) continue;
    record = { ...(await step.upgrade(record)), schemaVersion: step.from + 1 };
    await table.put(record);
  }
  return record;
}

/** Walks primary keys, which Dexie reads without the values, one record at a time. */
export async function migrateRecords<T extends VersionedRecord, K>(
  table: Table<T, K>,
  steps: MigrationStep<T>[],
  onError?: (err: unknown, key: K) => void,
): Promise<void> {
  for (const key of await table.toCollection().primaryKeys()) {
    try {
      await migrateRecord(table, key, steps);
    } catch (err) {
      onError?.(err, key);
    }
  }
}
